/**
 * Tabela de itens da DLQ de contratos
 * Com ações de reprocessar e descartar
 */

import { useState, Fragment } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ChevronDown, ChevronRight, FileJson, RefreshCw, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDlqContractsItems, useReprocessDlqContractsItem, useDiscardDlqContractsItem, DlqContractsItem } from '@/hooks/useDlqContractsMonitor';
import { toast } from 'sonner';

/** Linha expansível do item DLQ */
function DlqContractItemRow({ item }: { item: DlqContractsItem }) {
  const [isOpen, setIsOpen] = useState(false);
  const reprocessMutation = useReprocessDlqContractsItem();
  const discardMutation = useDiscardDlqContractsItem();
  const hasPayload = item.message && Object.keys(item.message).length > 0;

  const handleReprocess = async () => {
    try {
      await reprocessMutation.mutateAsync(item.msg_id);
      toast.success('Item reenviado para a fila principal');
    } catch { toast.error('Erro ao reprocessar item'); }
  };

  const handleDiscard = async () => {
    try {
      await discardMutation.mutateAsync(item.msg_id);
      toast.success('Item descartado com sucesso');
    } catch { toast.error('Erro ao descartar item'); }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Fragment>
        <TableRow className={cn('hover:bg-muted/30', isOpen && 'bg-muted/20')}>
          <TableCell className="font-mono text-xs">{item.msg_id}</TableCell>
          <TableCell><Badge variant="destructive">{item.dlq_reason}</Badge></TableCell>
          <TableCell><Badge variant="outline" className="font-mono">{item.action || '-'}</Badge></TableCell>
          <TableCell className="font-medium">{item.tenant_name}</TableCell>
          <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{item.contract_title || '-'}</TableCell>
          <TableCell className="text-sm text-muted-foreground">
            {formatDistanceToNow(new Date(item.enqueued_at), { addSuffix: true, locale: ptBR })}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={handleReprocess} disabled={reprocessMutation.isPending}
                className="h-7 px-2" title="Reprocessar">
                <RefreshCw className={`h-3 w-3 ${reprocessMutation.isPending ? 'animate-spin' : ''}`} />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive" title="Descartar">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Descartar item?</AlertDialogTitle>
                    <AlertDialogDescription>O item será arquivado permanentemente. Esta ação não pode ser desfeita.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDiscard}>Descartar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </TableCell>
          <TableCell>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
          </TableCell>
        </TableRow>
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/10 hover:bg-muted/10">
            <TableCell colSpan={8} className="p-0">
              <div className="p-4 space-y-3 border-t border-border/50">
                {item.dlq_error && (
                  <div className="text-sm"><span className="font-medium text-destructive">Erro:</span> <span className="text-muted-foreground">{item.dlq_error}</span></div>
                )}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium"><FileJson className="h-4 w-4 text-primary" /> Payload</div>
                  {hasPayload ? (
                    <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-x-auto text-muted-foreground max-h-[300px] overflow-y-auto">
                      {JSON.stringify(item.message, null, 2)}
                    </pre>
                  ) : <p className="text-sm text-muted-foreground">Sem payload</p>}
                </div>
              </div>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </Fragment>
    </Collapsible>
  );
}

export function DlqContractsItemsTable() {
  const { data: items, isLoading } = useDlqContractsItems(50, 0);

  if (isLoading) {
    return <Card><CardHeader><CardTitle>Itens da DLQ</CardTitle></CardHeader><CardContent><Skeleton className="h-[300px] w-full" /></CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2">📋 Itens da DLQ de Contratos</CardTitle></CardHeader>
      <CardContent>
        {!items || items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Nenhum item na DLQ de contratos. 🎉</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[70px]">ID</TableHead>
                  <TableHead className="w-[120px]">Razão</TableHead>
                  <TableHead className="w-[120px]">Ação</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Contrato</TableHead>
                  <TableHead className="w-[100px]">Criado</TableHead>
                  <TableHead className="w-[100px]">Ações</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(item => <DlqContractItemRow key={item.msg_id} item={item} />)}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
